<template>
    <div class="chat-with-us-page">
        <PageTitleBanner :title="$t('m.chatWithUsTitle')" showBack/>

        <main class="chat-with-us-body">
            <section class="chat-brand">
                <img v-if="brand_logo" class="chat-brand-logo" :src="brand_logo" alt="">
                <template v-else>
                    <img v-if="brand_favicon" class="chat-brand-icon" :src="brand_favicon" alt="">
                    <span class="chat-brand-name">{{ brand_name }}</span>
                </template>
            </section>

            <section
                v-if="contactItems.length"
                class="chat-contact-list"
                :aria-label="$t('m.chatContactMethods')"
            >
                <a
                    v-for="item in primaryContacts"
                    :key="item.key"
                    class="chat-contact-row interactive-surface"
                    :href="item.href"
                    :target="getLinkTarget(item)"
                    :rel="getLinkRel(item)"
                >
                    <component
                        :is="getPrimaryIcon(item)"
                        class="chat-contact-icon"
                        :class="item.type"
                    />
                    <span class="chat-contact-text">{{ item.displayText }}</span>
                </a>

                <div v-if="socialContacts.length" class="chat-social-list">
                    <a
                        v-for="item in socialContacts"
                        :key="item.key"
                        class="chat-social-link interactive-surface"
                        :class="item.type"
                        :href="item.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        :aria-label="getSocialLabel(item)"
                    >
                        <component
                            :is="getSocialIcon(item)"
                            class="chat-social-icon"
                        />
                    </a>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import PageTitleBanner from "@/components/PageTitleBanner.vue";
import EmailIcon from "@/components/icons/EmailIcon.vue";
import LanguageIcon from "@/components/icons/LanguageIcon.vue";
import PhoneIcon from "@/components/icons/PhoneIcon.vue";
import FacebookIcon from "@/components/icons/social/FacebookIcon.vue";
import InstagramIcon from "@/components/icons/social/InstagramIcon.vue";
import LinkedinIcon from "@/components/icons/social/LinkedinIcon.vue";
import TiktokIcon from "@/components/icons/social/TiktokIcon.vue";
import TwitterIcon from "@/components/icons/social/TwitterIcon.vue";
import WhatsappIcon from "@/components/icons/social/WhatsappIcon.vue";
import YoutubeIcon from "@/components/icons/social/YoutubeIcon.vue";
import { getAboutUsContactItems } from "@/utils/aboutUs";

export default {
    name: "ChatWithUs",
    components: {
        EmailIcon,
        FacebookIcon,
        InstagramIcon,
        LanguageIcon,
        LinkedinIcon,
        PageTitleBanner,
        PhoneIcon,
        TiktokIcon,
        TwitterIcon,
        WhatsappIcon,
        YoutubeIcon,
    },
    computed: {
        ...mapGetters("appConfig", ["brand_name", "brand_logo", "brand_favicon", "about_us"]),
        contactItems() {
            return getAboutUsContactItems(this.about_us);
        },
        primaryContacts() {
            return this.contactItems.filter(item => item.isPrimary);
        },
        socialContacts() {
            return this.contactItems.filter(item => !item.isPrimary);
        },
    },
    methods: {
        getLinkTarget(item) {
            return item.type === "website" ? "_blank" : undefined;
        },
        getLinkRel(item) {
            return item.type === "website" ? "noopener noreferrer" : undefined;
        },
        getPrimaryIcon(item) {
            const iconMap = {
                website: "LanguageIcon",
                phone: "PhoneIcon",
                email: "EmailIcon",
            };

            return iconMap[item.type] || "LanguageIcon";
        },
        getSocialLabel(item) {
            return item.type;
        },
        getSocialIcon(item) {
            const iconMap = {
                whatsapp: "WhatsappIcon",
                facebook: "FacebookIcon",
                instagram: "InstagramIcon",
                linkedin: "LinkedinIcon",
                tiktok: "TiktokIcon",
                twitter: "TwitterIcon",
                youtube: "YoutubeIcon",
            };

            return iconMap[item.type] || "FacebookIcon";
        },
    },
};
</script>

<style lang="scss">
.chat-with-us-page {
    min-height: 100vh;
    background: #fff;
}

.chat-with-us-body {
    min-height: calc(100vh - 44px);
    padding: 64px 20px 40px;
}

.chat-brand {
    width: 100%;
    min-height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.chat-brand-logo {
    display: block;
    width: auto;
    height: auto;
    max-width: min(100%, 240px);
    max-height: 72px;
    object-fit: contain;
    object-position: center center;
    margin: 0 auto;
}

.chat-brand-icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
}

.chat-brand-name {
    color: #353a64;
    font-family: SF Pro Text, Avenir, Helvetica, Arial, sans-serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
}

.chat-contact-list {
    margin-top: 72px;
}

.chat-contact-row {
    height: 54px;
    border-bottom: 1px solid #f2f2f2;
    display: flex;
    align-items: center;
    padding: 0 4px;
    color: #333;
    text-decoration: none;
    font-family: SF Pro Text, Avenir, Helvetica, Arial, sans-serif;
}

.chat-contact-icon {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    margin-right: 14px;
    color: #333;
}

.chat-contact-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: var(--theme-selectable-accent-color, #276ce1);
    font-size: 14px;
    line-height: 20px;
    text-decoration: underline;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chat-social-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 18px 4px 20px;
}

.chat-social-link {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: var(--theme-selectable-accent-bg, #fafcff);
    color: #333;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chat-social-icon {
    width: 20px;
    height: 20px;
}
</style>
